// MultiSelect.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  alpha,
} from "@mui/material";
import { Close, KeyboardArrowDown, Check } from "@mui/icons-material";

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  label,
  placeholder = "Select...",
  isLoading = false,
  disabled = false,
  error = false,
  helperText = "",
  required = false,
  fullWidth = true,
  getOptionLabel = (opt) => opt?.label || opt?.name || "",
  getOptionValue = (opt) => opt?.value || opt?.id,
  noOptionsText = "No options found",
  searchable = false,
  onSearchChange,
  minChars = 2,
  loadingText = "Searching...",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchInput = (e) => {
    const newValue = e.target.value;
    setSearch(newValue);
    if (!open) setOpen(true);

    if (searchable && onSearchChange) {
      onSearchChange(newValue);
    }
  };

  const filteredOptions = searchable
    ? options
    : options.filter((opt) =>
        getOptionLabel(opt).toLowerCase().includes(search.toLowerCase())
      );

  const showDropdown = open && !disabled;

  const shouldShowMinCharsHint =
    searchable && search.length > 0 && search.length < minChars;

  const toggleSelect = (option) => {
    const isSelected = selected.some(
      (v) => getOptionValue(v) === getOptionValue(option)
    );
    let newValue;
    if (isSelected) {
      newValue = selected.filter(
        (v) => getOptionValue(v) !== getOptionValue(option)
      );
    } else {
      newValue = [...selected, option];
    }
    onChange(newValue);
    setSearch("");
    if (searchable && onSearchChange) {
      onSearchChange("");
    }
  };

  const handleRemove = (e, option) => {
    e.stopPropagation();
    const newValue = selected.filter(
      (v) => getOptionValue(v) !== getOptionValue(option)
    );
    onChange(newValue);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setSearch("");
    if (searchable && onSearchChange) {
      onSearchChange("");
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: fullWidth ? "100%" : "auto",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {label && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.8,
            fontWeight: 600,
            color: error ? "#EF4444" : "#64748B",
          }}
        >
          {label} {required && <span style={{ color: "#EF4444" }}>*</span>}
        </Typography>
      )}

      <Box
        onClick={() => !disabled && setOpen(!open)}
        sx={{
          minHeight: "42px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          padding: "6px 12px",
          borderRadius: "10px",
          border: `1px solid ${error ? "#EF4444" : open ? "#3B82F6" : "#E2E8F0"}`,
          bgcolor: "#FFFFFF",
          boxShadow: open ? `0 0 0 3px ${alpha("#3B82F6", 0.15)}` : "none",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: error ? "#EF4444" : disabled ? "#E2E8F0" : "#3B82F6",
          },
        }}
      >
        {selected.map((item) => (
          <Chip
            key={getOptionValue(item)}
            label={getOptionLabel(item)}
            size="small"
            onDelete={(e) => handleRemove(e, item)}
            deleteIcon={
              <Close sx={{ fontSize: "12px !important", color: "#3B82F6" }} />
            }
            sx={{
              bgcolor: alpha("#3B82F6", 0.08),
              color: "#3B82F6",
              fontWeight: 500,
              borderRadius: "6px",
              height: "24px",
              "& .MuiChip-deleteIcon:hover": { color: "#1D4ED8" },
            }}
          />
        ))}

        <input
          type="text"
          value={search}
          placeholder={selected.length === 0 ? placeholder : ""}
          onChange={handleSearchInput}
          onFocus={() => !disabled && setOpen(true)}
          disabled={disabled}
          style={{
            flex: 1,
            minWidth: "60px",
            border: "none",
            outline: "none",
            fontSize: "14px",
            color: "#1E293B",
            background: "transparent",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
          {isLoading ? (
            <CircularProgress
              size={16}
              color="inherit"
              sx={{ color: "#94A3B8" }}
            />
          ) : (
            <>
              {selected.length > 0 && (
                <IconButton size="small" onClick={handleClearAll} sx={{ p: 0.2 }}>
                  <Close sx={{ fontSize: 16, color: "#94A3B8" }} />
                </IconButton>
              )}
              <KeyboardArrowDown
                sx={{
                  fontSize: 20,
                  color: "#94A3B8",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </>
          )}
        </Box>
      </Box>

      {error && helperText && (
        <Typography
          variant="caption"
          sx={{ color: "#EF4444", display: "block", mt: 0.5, pl: 0.5 }}
        >
          {helperText}
        </Typography>
      )}

      {showDropdown && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "240px",
            overflowY: "auto",
            bgcolor: "#FFFFFF",
            borderRadius: "10px",
            border: "1px solid #E2E8F0",
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            animation: "fadeIn 0.15s ease-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(-4px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                py: 2,
                px: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={16} sx={{ color: "#3B82F6" }} />
              <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.8rem" }}>
                {loadingText}
              </Typography>
            </Box>
          ) : shouldShowMinCharsHint ? (
            <Box sx={{ py: 2, px: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#94A3B8", fontSize: "0.8rem" }}>
                Type at least {minChars} characters to search
              </Typography>
            </Box>
          ) : filteredOptions.length === 0 ? (
            <Box sx={{ py: 2, px: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                {noOptionsText}
              </Typography>
            </Box>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selected.some(
                (v) => getOptionValue(v) === getOptionValue(opt)
              );
              return (
                <Box
                  key={getOptionValue(opt)}
                  onClick={() => toggleSelect(opt)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.2,
                    px: 2,
                    cursor: "pointer",
                    fontSize: "14px",
                    color: isSelected ? "#3B82F6" : "#334155",
                    bgcolor: isSelected ? alpha("#3B82F6", 0.04) : "transparent",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      bgcolor: alpha("#3B82F6", 0.08),
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                    {getOptionLabel(opt)}
                  </Typography>
                  {isSelected && <Check sx={{ fontSize: 16, color: "#3B82F6" }} />}
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

export default MultiSelect;